import { useState, useCallback, useRef, useEffect } from "react";

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlockedAt?: number;
}

export interface SessionStats {
  notesPlayed: number;
  correctNotes: number;
  wrongNotes: number;
  accuracy: number;
  streak: number;
  bestStreak: number;
  xpEarned: number;
  startTime: number;
  exercisesCompleted: number;
}

export interface GameState {
  level: number;
  totalXp: number;
  xpToNextLevel: number;
  currentXp: number;
  totalNotesPlayed: number;
  totalCorrect: number;
  bestStreak: number;
  exercisesCompleted: number;
  achievements: Achievement[];
  dailyStreak: number;
  lastPlayedDate: string;
}

const ACHIEVEMENTS: Achievement[] = [
  { id: "first_note", title: "First Note", description: "Play your first correct note", icon: "music" },
  { id: "streak_10", title: "On Fire", description: "Get a 10-note streak", icon: "flame" },
  { id: "streak_25", title: "Unstoppable", description: "Get a 25-note streak", icon: "flame" },
  { id: "streak_50", title: "Virtuoso", description: "Get a 50-note streak", icon: "flame" },
  { id: "perfect_exercise", title: "Perfect Score", description: "Complete an exercise with 100% accuracy", icon: "star" },
  { id: "exercises_5", title: "Dedicated", description: "Complete 5 exercises", icon: "trophy" },
  { id: "exercises_25", title: "Committed", description: "Complete 25 exercises", icon: "trophy" },
  { id: "notes_100", title: "Century", description: "Play 100 correct notes", icon: "target" },
  { id: "notes_500", title: "Half Thousand", description: "Play 500 correct notes", icon: "target" },
  { id: "notes_1000", title: "Thousand Club", description: "Play 1000 correct notes", icon: "target" },
  { id: "daily_3", title: "Consistent", description: "Practice 3 days in a row", icon: "calendar" },
  { id: "daily_7", title: "Weekly Warrior", description: "Practice 7 days in a row", icon: "calendar" },
  { id: "level_5", title: "Rising Star", description: "Reach level 5", icon: "star" },
  { id: "level_10", title: "Maestro", description: "Reach level 10", icon: "star" },
  { id: "accuracy_90", title: "Sharp Eye", description: "Maintain 90%+ accuracy in a session of 20+ notes", icon: "eye" },
];

function xpForLevel(level: number): number {
  return Math.floor(100 * Math.pow(1.3, level - 1));
}

const STORAGE_KEY = "midi-sight-reader-game-state";

const DEFAULT_GAME_STATE: GameState = {
  level: 1,
  totalXp: 0,
  xpToNextLevel: xpForLevel(1),
  currentXp: 0,
  totalNotesPlayed: 0,
  totalCorrect: 0,
  bestStreak: 0,
  exercisesCompleted: 0,
  achievements: [],
  dailyStreak: 0,
  lastPlayedDate: "",
};

function asNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

// Stored state may come from an older app version or be corrupted;
// merge field-by-field so missing/invalid values fall back to defaults.
function loadGameState(): GameState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<GameState>;
      const level = Math.max(1, Math.floor(asNumber(parsed.level, 1)));
      return {
        level,
        totalXp: asNumber(parsed.totalXp, 0),
        xpToNextLevel: xpForLevel(level),
        currentXp: asNumber(parsed.currentXp, 0),
        totalNotesPlayed: asNumber(parsed.totalNotesPlayed, 0),
        totalCorrect: asNumber(parsed.totalCorrect, 0),
        bestStreak: asNumber(parsed.bestStreak, 0),
        exercisesCompleted: asNumber(parsed.exercisesCompleted, 0),
        achievements: Array.isArray(parsed.achievements) ? parsed.achievements : [],
        dailyStreak: asNumber(parsed.dailyStreak, 0),
        lastPlayedDate: typeof parsed.lastPlayedDate === "string" ? parsed.lastPlayedDate : "",
      };
    }
  } catch {}
  return { ...DEFAULT_GAME_STATE };
}

function saveGameState(state: GameState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function useGameState() {
  const [gameState, setGameState] = useState<GameState>(loadGameState);
  const [session, setSession] = useState<SessionStats>({
    notesPlayed: 0,
    correctNotes: 0,
    wrongNotes: 0,
    accuracy: 100,
    streak: 0,
    bestStreak: 0,
    xpEarned: 0,
    startTime: Date.now(),
    exercisesCompleted: 0,
  });
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([]);
  const [showLevelUp, setShowLevelUp] = useState(false);

  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  useEffect(() => {
    saveGameState(gameState);
  }, [gameState]);

  const checkAchievements = useCallback(
    (gs: GameState, sess: SessionStats): Achievement[] => {
      const unlocked: Achievement[] = [];
      const unlockedIds = new Set(gs.achievements.map((a) => a.id));

      const checks: [string, boolean][] = [
        ["first_note", gs.totalCorrect >= 1],
        ["streak_10", sess.bestStreak >= 10 || gs.bestStreak >= 10],
        ["streak_25", sess.bestStreak >= 25 || gs.bestStreak >= 25],
        ["streak_50", sess.bestStreak >= 50 || gs.bestStreak >= 50],
        ["exercises_5", gs.exercisesCompleted >= 5],
        ["exercises_25", gs.exercisesCompleted >= 25],
        ["notes_100", gs.totalCorrect >= 100],
        ["notes_500", gs.totalCorrect >= 500],
        ["notes_1000", gs.totalCorrect >= 1000],
        ["daily_3", gs.dailyStreak >= 3],
        ["daily_7", gs.dailyStreak >= 7],
        ["level_5", gs.level >= 5],
        ["level_10", gs.level >= 10],
        ["accuracy_90", sess.notesPlayed >= 20 && sess.accuracy >= 90],
      ];

      for (const [id, condition] of checks) {
        if (!unlockedIds.has(id) && condition) {
          const achievement = ACHIEVEMENTS.find((a) => a.id === id);
          if (achievement) {
            unlocked.push({ ...achievement, unlockedAt: Date.now() });
          }
        }
      }

      return unlocked;
    },
    []
  );

  // Surface level-ups and new achievements outside the state computation.
  const announceLevelUp = useCallback(() => {
    setShowLevelUp(true);
    setTimeout(() => setShowLevelUp(false), 3000);
  }, []);

  const announceAchievements = useCallback((unlocked: Achievement[]) => {
    setNewAchievements((prev) => [...prev, ...unlocked]);
    setTimeout(() => setNewAchievements([]), 4000);
  }, []);

  // State is computed from refs (kept in sync below) instead of inside
  // setState updaters: updaters must stay pure, and side effects there would
  // double-fire under StrictMode.
  const recordNote = useCallback(
    (correct: boolean) => {
      const prevSession = sessionRef.current;
      const xpGain = correct ? 10 + Math.floor(prevSession.streak / 5) * 5 : 0;

      const newSession: SessionStats = {
        ...prevSession,
        notesPlayed: prevSession.notesPlayed + 1,
        correctNotes: prevSession.correctNotes + (correct ? 1 : 0),
        wrongNotes: prevSession.wrongNotes + (correct ? 0 : 1),
        streak: correct ? prevSession.streak + 1 : 0,
        bestStreak: correct
          ? Math.max(prevSession.bestStreak, prevSession.streak + 1)
          : prevSession.bestStreak,
        xpEarned: prevSession.xpEarned + xpGain,
      };
      newSession.accuracy =
        newSession.notesPlayed > 0
          ? Math.round((newSession.correctNotes / newSession.notesPlayed) * 100)
          : 100;
      sessionRef.current = newSession;
      setSession(newSession);

      const prev = gameStateRef.current;
      if (!correct) {
        const newState = { ...prev, totalNotesPlayed: prev.totalNotesPlayed + 1 };
        gameStateRef.current = newState;
        setGameState(newState);
        return;
      }

      let newState: GameState = {
        ...prev,
        totalNotesPlayed: prev.totalNotesPlayed + 1,
        totalCorrect: prev.totalCorrect + 1,
        totalXp: prev.totalXp + xpGain,
        currentXp: prev.currentXp + xpGain,
        bestStreak: Math.max(prev.bestStreak, newSession.streak),
      };

      // Level up check
      let leveledUp = false;
      while (newState.currentXp >= newState.xpToNextLevel) {
        newState = {
          ...newState,
          currentXp: newState.currentXp - newState.xpToNextLevel,
          level: newState.level + 1,
          xpToNextLevel: xpForLevel(newState.level + 1),
        };
        leveledUp = true;
      }

      // Daily streak
      const today = new Date().toISOString().split("T")[0];
      if (newState.lastPlayedDate !== today) {
        const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
        newState = {
          ...newState,
          dailyStreak:
            newState.lastPlayedDate === yesterday ? newState.dailyStreak + 1 : 1,
          lastPlayedDate: today,
        };
      }

      // Check achievements
      const newAch = checkAchievements(newState, newSession);
      if (newAch.length > 0) {
        newState = {
          ...newState,
          achievements: [...newState.achievements, ...newAch],
        };
      }

      gameStateRef.current = newState;
      setGameState(newState);
      if (leveledUp) announceLevelUp();
      if (newAch.length > 0) announceAchievements(newAch);
    },
    [checkAchievements, announceLevelUp, announceAchievements]
  );

  const completeExercise = useCallback(
    (accuracy: number) => {
      const bonusXp = Math.floor(accuracy * 0.5);

      const newSession: SessionStats = {
        ...sessionRef.current,
        exercisesCompleted: sessionRef.current.exercisesCompleted + 1,
        xpEarned: sessionRef.current.xpEarned + bonusXp,
      };
      sessionRef.current = newSession;
      setSession(newSession);

      const prev = gameStateRef.current;
      let newState: GameState = {
        ...prev,
        exercisesCompleted: prev.exercisesCompleted + 1,
        totalXp: prev.totalXp + bonusXp,
        currentXp: prev.currentXp + bonusXp,
      };

      let leveledUp = false;
      while (newState.currentXp >= newState.xpToNextLevel) {
        newState = {
          ...newState,
          currentXp: newState.currentXp - newState.xpToNextLevel,
          level: newState.level + 1,
          xpToNextLevel: xpForLevel(newState.level + 1),
        };
        leveledUp = true;
      }

      const unlocked: Achievement[] = [];

      // Perfect exercise achievement (not covered by checkAchievements)
      if (accuracy === 100) {
        const unlockedIds = new Set(newState.achievements.map((a) => a.id));
        if (!unlockedIds.has("perfect_exercise")) {
          const ach = ACHIEVEMENTS.find((a) => a.id === "perfect_exercise");
          if (ach) unlocked.push({ ...ach, unlockedAt: Date.now() });
        }
      }

      // Exercise-count (and other) achievements unlock here too, not just on
      // the next correct note.
      unlocked.push(...checkAchievements(newState, newSession));

      if (unlocked.length > 0) {
        newState = {
          ...newState,
          achievements: [...newState.achievements, ...unlocked],
        };
      }

      gameStateRef.current = newState;
      setGameState(newState);
      if (leveledUp) announceLevelUp();
      if (unlocked.length > 0) announceAchievements(unlocked);
    },
    [checkAchievements, announceLevelUp, announceAchievements]
  );

  const resetSession = useCallback(() => {
    const fresh: SessionStats = {
      notesPlayed: 0,
      correctNotes: 0,
      wrongNotes: 0,
      accuracy: 100,
      streak: 0,
      bestStreak: 0,
      xpEarned: 0,
      startTime: Date.now(),
      exercisesCompleted: 0,
    };
    sessionRef.current = fresh;
    setSession(fresh);
  }, []);

  const dismissAchievement = useCallback(() => {
    setNewAchievements([]);
  }, []);

  return {
    gameState,
    session,
    newAchievements,
    showLevelUp,
    recordNote,
    completeExercise,
    resetSession,
    dismissAchievement,
    allAchievements: ACHIEVEMENTS,
  };
}
